import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { VibeAuditEdgeReachabilityReadSchema } from "./read-vocabularies.schema";

/**
 * Emitted when the agent's edge reachability probe finds a RUNNING app that the
 * tenant's edge has no route to. Written on the TRANSITION into UNROUTED — the
 * probe re-asks once a minute for as long as it stays broken, so a row per
 * observation would bury the event under its own repetitions.
 *
 * The payload has to stand alone: the app's `edgeReachability` column carries
 * only the CURRENT state, so by the time anyone reads this row the app may have
 * recovered, and the previous state that makes it a transition is gone. Hence
 * `previousReachability` and the deployment that was healthy at the time — the
 * pair that says "this was fine, and this workload was running, and it stopped
 * being reachable anyway".
 */
export const VibeAuditAppEdgeUnroutedPayloadSchema = z.object({
  eventType: z.literal("APP_EDGE_UNROUTED"),
  /// The healthy deployment the probe was justified by. Not what the event is
  /// ABOUT — reachability is a fact about the host — but without it the row
  /// cannot say that a workload was actually running when the edge lost it.
  vibeDeploymentId: z.string().uuid(),
  /// What the app's reachability was before this observation. Null when the app
  /// had never been probed, which is the ordinary case for a first-ever
  /// observation. Derived from the enum (no hardcoded literals).
  previousReachability: z.enum(DbEnum.VIBE_APP_EDGE_REACHABILITY_VALUES).nullable(),
  /// The edge's own reason string, as the probe received it. Diagnostics, never
  /// a verdict — the event type is the verdict.
  detail: z.string().max(2000).nullable()
});
export type VibeAuditAppEdgeUnroutedPayload = z.infer<typeof VibeAuditAppEdgeUnroutedPayloadSchema>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
export const VibeAuditAppEdgeUnroutedReadPayloadSchema =
  VibeAuditAppEdgeUnroutedPayloadSchema.extend({
    previousReachability: VibeAuditEdgeReachabilityReadSchema.nullable()
  });
